/**
 * Phase 3 convergence anchor — the ONE shared derivation of a task answer's
 * durable per-message-doc key.
 *
 * Kept in its own ZERO-dependency module (no Firestore, no firebase/auth, no
 * React) so the pure reducer/persist hot paths — `interruptedTurn.ts`,
 * `streamFinalizePersist.ts` — can freeze the anchor without dragging the
 * firebase SDK into their import graph. `chatThreadMessageDocFirestore.ts`
 * re-exports it so existing call sites are unchanged.
 */

/**
 * The stable per-doc key for a server-backed answer: `prest0n_task_<serverTaskId>`.
 * Returns null when there is no server task (a plain message keys by its own id).
 * This exact format is what the server stamps as `recoveredMessageId`, so the
 * client and server writers converge on a single Firestore document.
 */
export function deriveTaskAnchorId(serverTaskId: string | null | undefined): string | null {
  const t = typeof serverTaskId === 'string' ? serverTaskId.trim() : '';
  return t ? `prest0n_task_${t}` : null;
}

/**
 * The shared-store id of the HUMAN turn that opened a task:
 * `prest0n_task_<taskId>__user` — the client twin of the server's
 * `userTurnAnchorId` (functions/src/prest0nSession/groupThreadMessageDoc.ts),
 * parity-pinned by tests/prest0nSession/groupThreadMessageDoc.parity.test.ts.
 * Minted on the client BEFORE the bubble exists (PR-2, 2026-09-08) so the
 * delivery ledger, the group-store echo and the server doc all name one row.
 */
export function userTurnAnchorId(taskId: string | null | undefined): string | null {
  const t = typeof taskId === 'string' ? taskId.trim() : '';
  return t ? `prest0n_task_${t}__user` : null;
}

/**
 * The send instant encoded in a client-minted task id (`task_<base36 ms>_<rand>`,
 * see mintClientTaskId). Null for any other shape — callers fall back to now.
 */
export function orderTsFromClientTaskId(taskId: string | null | undefined): number | null {
  const t = typeof taskId === 'string' ? taskId.trim() : '';
  const m = /^task_([0-9a-z]+)_[0-9a-z]+$/i.exec(t);
  if (!m) return null;
  const ms = Number.parseInt(m[1], 36);
  return Number.isFinite(ms) && ms > 0 ? ms : null;
}
